/**
 * Lib.Common: v-size directive
 *
 * @file
 * Allows a component to react to the size of an element. Typically, this would be used similarly to a CSS container
 * query, except that it has universal browser support and the ability to trigger code. Internally, it is implemented
 * using `ResizeObserver` where available, with a fallback that involves additional DOM elements for older browsers.
 *
 * This directive will always apply the class or call the bound function once on init (i.e. the mount of the containing
 * component). If a callback function is provided, it will then trigger potentially many times when a user is actively
 * resizing a window. For that reason, it is preferred to use the object (class) syntax. If you are providing a
 * function, it is better to avoid triggering a re-render every time the size changes. You can accomplish this by
 * writing to a variable that represents whether the size fits some criteria; the value would only actually change at
 * some breakpoint. Avoid creating a callback that directly writes the width to a variable that is then used in the
 * template.
 *
 * This directive reports border box dimensions, as Lib.Common applications default to border box sizing.
 *
 * Use this directive with elements that have position: `relative`, `absolute`, `fixed`, or `sticky`.
 *
 * If you need to react to both width and height, use the callback function syntax.
 *
 * @modifier width - React to width. This is the default for the class syntax.
 * @modifier height - React to height.
 *
 * @value {object|function} - An object that defines a list of classes and their breakpoints, or a function to call
 * with width and height arguments. Breakpoints are specified as two-element arrays; the first element is the lower
 * bound, inclusive, of pixel sizes that the class applies to; the second element is the upper bound, noninclusive.
 *
 * @example
 * <template lang="pug">
 * div(v-size="{ 'fg-error': [,360], 'fg-warning': [360,480], 'fg-ok': [480,] }")
 *    | Resize the window to change color.
 * </template>
 */


const haveResizeObserver = $build.platform == 'android' || !!window.ResizeObserver;


function div(className, parent, scroll) {
    let el = document.createElement('div');
    el.className = className;
    if (parent) {
        parent.appendChild(el);
    }
    if (scroll) {
        el.addEventListener('scroll', scroll);
    }
    return el;
}


function scrollReset(el) {
    const rso = el._rso;
    if (rso.e) {
        rso.ee.scrollTop =
        rso.ee.scrollLeft =
        rso.es.scrollTop =
        rso.es.scrollLeft = 8192;
    }
}


function scrollHandler() {
    const target = this;
    const rect = target.getBoundingClientRect();
    scrollReset(target);
    target._rso.fn(
        rect.width,
        rect.height
    );
}


function borderSizeHandler(entries) {
    const entry = entries[entries.length - 1];
    if (entry) {
        const target = entry.target;
        const borderBoxSize = entry.borderBoxSize;
        if (Array.isArray(borderBoxSize)) {
            target._rso.fn(
                borderBoxSize[0].inlineSize,
                borderBoxSize[0].blockSize
            );
        } else {
            target._rso.fn(
                borderBoxSize.inlineSize,
                borderBoxSize.blockSize
            );
        }
    }
}


function borderRectHandler(entries) {
    const entry = entries[entries.length - 1];
    if (entry) {
        const target = entry.target;
        const rect = target.getBoundingClientRect();
        target._rso.fn(
            rect.width,
            rect.height
        );
    }
}


function bind(el, binding) {
    const val = binding.value;
    const mod = binding.modifiers;
    let fn;
    if (typeof val === 'function') {
        fn = val;
    } else {
        const dim = mod.height ? 'h' : 'w';
        fn = new Function('w', 'h', Object.keys(val).reduce(
            (body,key) => body + 'if(' + (val[key][1] != null ? dim + '<' + val[key][1] + (val[key][0] ? '&&' : '') : '') + (val[key][0] != null ? dim + '>=' + val[key][0] : '') + '){' +
                'this.classList.add("' + key + '");this._rso.cls.push("' + key + '");' +
            '}else{' +
                'this.classList.remove("' + key + '")' +
            '};', 'this._rso.cls=[];')
        ).bind(el);
    }
    if (haveResizeObserver) {
        const obs = new ResizeObserver(
            'borderBoxSize' in ResizeObserverEntry.prototype
                ? borderSizeHandler
                : borderRectHandler);
        obs.observe(el, {
            box: 'border-box'
        });
        el._rso = {
            obs,
            fn,
            cls: []
        };
    } else {
        const cls =
            mod.width && !mod.height ? '_rsew' :
            mod.height && !mod.width ? '_rseh' : '_rsew _rseh';
        const e = div(cls);
        const scroll = scrollHandler.bind(el);
        const ee = div(cls, e, scroll);
        const es = div(cls, e, scroll);
        div('_rsee', ee);
        div('_rses', es);
        el._rso = {
            e,
            ee,
            es,
            fn,
            cls: []
        };
    }
}


function update(el, binding) {
    const rso = el._rso;
    if (rso) {
        if (rso.e) {
            el.appendChild(rso.e);
            Vue.nextTick(() => { scrollReset(el) });
        }
        if (rso.cls.length) {
            el.classList.add(...rso.cls);
        }
        // callback is called on bind - duplicate this behavior if this instance is being reused
        if (binding.value !== binding.oldValue) {
            const rect = el.getBoundingClientRect();
            rso.fn(rect.width, rect.height);
        }
    }
}


function unbind(el) {
    const rso = el._rso;
    if (rso) {
        if (rso.obs) {
            rso.obs.disconnect();
        }
        if (rso.e) {
            el.removeChild(rso.e);
        }
        if (rso.cls.length) {
            el.classList.remove(...rso.cls);
        }
        delete el._rso;
    }
}


export default {
    name: 'size',
    directive: {
        bind,
        unbind,
        update
    }
};
